import { useStore } from '../../lib/hooks/useStore';
import { PaymentCheckServerProps } from '../../lib/typegen/payment-check-server-props';
import style from './style.module.scss';
import { useEffect, useState, useCallback, useRef } from 'react';
import { devices } from '../../lib/devices/devices';
import { DeviceStatusFlags } from '../../lib/devices/MonitoredDevice';
import logger from '../../lib/helpers/logger';
import { CISConfigType } from '../../lib/typegen/cis-config-type';
import BalancesKvpList from '../balances-kvp-list';
import MessageBoxSet from '../message-box-set';
import checkScannerImage from '../../assets/check-scanner.svg';
import api from '../../lib/api/api';
import { PPConfigType } from '../../lib/typegen/pp-config-type';
import { PayWithCheckRequest } from '../../lib/typegen/pay-with-check-request';
import { LegacyPostAcceptedPayment, LegacyPostRejectedPayment } from '../../lib/types/legacyAdminApi';
import passKeyHelper from '../../lib/helpers/passKey';
import { v4 as guid } from 'uuid';
import { PaymentError } from '../../lib/types/paymentCardState';
import MainMessageBox from '../../features/main-message-box/main-message-box';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import Confirm from '../confirm';
import CheckReaderDevice from '../../lib/devices/check-reader/CheckReaderDevice';
import { PaymentType } from '../../lib/typegen/payment-type';
import enumHelper from '../../lib/helpers/enums';
import { PayWithCheckResponse } from '../../lib/typegen/pay-with-check-response';
import GetCheckView from './components/GetCheckView';
import { useSessionCleanup } from '../../lib/hooks/useSessionCleanup';
import { AppEnvironment } from '../../lib/typegen/app-environment';
import KioAppHelper from '../../lib/helpers/kioApp';
import ServiceFeeView from '../service-fee-view';
import { parseAmountToNumber } from '../../lib/helpers/math';
import { PaymentFeeViewServerProps } from '../../lib/typegen/payment-fee-view-server-props';
import { extractCheckNumberFromMicr } from '../../lib/helpers/micr';
import { getApiErrorMessage } from '../../lib/helpers/apiError';

type PaymentCheckProps = PaymentCheckServerProps;

enum PaymentCheckState {
	AWAITING_INSERT,
	PROCESSING,
	ERROR,
	AWAITING_REMOVAL,
	DONE,
	SERVICE_FEE,
}

interface CheckData {
	imagePath: string;
	micr: string;
	account: string;
	routing: string;
	checkNumber?: string;
}

const WAIT_FOR_TAKE_CHECK_MS = 500;
const TAKE_CHECK_REMINDER_MIN_MS = 30000;
const PAYMENT_CHECK_REGISTRY_KEY = 'payment-check';

const PaymentCheck = function ({
	nextPageTarget,
	previousPageTarget,
	stampVoid,
	imageAltKey,
	paymentTotalLabelKey,
	infoBoxLabelKey,
	warningBoxLabelKey,
	errorLabel,
	paymentFeeViewServerProps,
	enablePreReceiptCheckRemovalPrompt,
}: PaymentCheckProps) {
	const {
		accountStore: {
			cartItems,
			setPaymentType,
			cartTotalAmount,
			transactionFee,
			setTransactionFee,
			cartId,
			setCartId,
			token,
			setToken,
			hasPreparedTransaction,
			setHasPreparedTransaction,
			payorInformation,
			setPreReceiptCheckRemovalPromptHandled,
		},
		sessionStore: { legacyId, unregisterCleanup, setSkipCountdownModal, setTimeoutNavigationCallback, beginKioWareSession },
		kioskStore: { kioskId, companyId },
		uiStore: { handleNavigation, setBackEnabled, setCanCancel, showLoader, clearCustomBackAction },
		configStore: { getString, envDeviceOption, cisType, ppType, environment, kioskLocation, config },
	} = useStore();
	const [paymentState, setPaymentState] = useState(
		(ppType === PPConfigType.GrantStreet ||
			ppType === PPConfigType.InvoiceCloudV1 ||
			ppType === PPConfigType.PayGo ||
			ppType === PPConfigType.MyUsage) &&
		transactionFee
			? PaymentCheckState.SERVICE_FEE
			: PaymentCheckState.AWAITING_INSERT
	);
	const [error, setError] = useState<PaymentError | null>(null);
	const [checkReader, setCheckReader] = useState<CheckReaderDevice | null>(null);
	const [isCleanupRequired, setIsCleanupRequired] = useState<boolean>(false);
	const [checkData, setCheckData] = useState<CheckData | null>(null);
	const isPreparingTransaction = useRef(false);
	const hasPreparedTransactionRef = useRef(hasPreparedTransaction);
	hasPreparedTransactionRef.current = hasPreparedTransaction;

	const [isPrepared, setIsPrepared] = useState(false);
	const isPreparedRef = useRef(false);
	isPreparedRef.current = isPrepared;
	const isPayGoCustomer = cisType === CISConfigType.PayGo || ppType === PPConfigType.PayGo;
	const isMyUsageCustomer = cisType === CISConfigType.MyUsage || ppType === PPConfigType.MyUsage;
	const supportsPrePayFlag = isPayGoCustomer || isMyUsageCustomer;
	const takeCheckReminderStartedAtRef = useRef<number | null>(null);

	useEffect(() => setPaymentType(PaymentType.Check), [setPaymentType]);

	useEffect(() => {
		setPreReceiptCheckRemovalPromptHandled(false);
	}, [setPreReceiptCheckRemovalPromptHandled]);

	useEffect(() => {
		if (paymentState === PaymentCheckState.AWAITING_REMOVAL) {
			takeCheckReminderStartedAtRef.current = Date.now();
			setSkipCountdownModal(true);
			setTimeoutNavigationCallback(null);
			beginKioWareSession();
			const autoAdvanceHandle = setTimeout(() => {
				setPaymentState(PaymentCheckState.DONE);
			}, TAKE_CHECK_REMINDER_MIN_MS);
			return () => {
				clearTimeout(autoAdvanceHandle);
				takeCheckReminderStartedAtRef.current = null;
				setSkipCountdownModal(false);
				setTimeoutNavigationCallback(null);
			};
		}
	}, [paymentState, setSkipCountdownModal, setTimeoutNavigationCallback, beginKioWareSession]);

	const prepareTransaction = useCallback(async () => {
		logger.logGeneral.log(
			{},
			`[prepareTransaction] called — ppType: ${ppType}, hasPreparedTransaction: ${hasPreparedTransaction}, isPreparingTransaction.current: ${isPreparingTransaction.current}`
		);
		// Check ppType directly instead of relying on state
		const requiresPrepare =
			ppType === PPConfigType.GrantStreet ||
			ppType === PPConfigType.InvoiceCloudV1 ||
			ppType === PPConfigType.PayGo ||
			ppType === PPConfigType.MyUsage;
		if (!requiresPrepare) {
			console.log('[prepareTransaction] not required for ppType, skipping');
			logger.logGeneral.log({}, 'Prepare transaction not required for this PP config, skipping...');
			return;
		}

		// Use ref to prevent concurrent calls without triggering re-renders
		if (isPreparingTransaction.current || hasPreparedTransaction) {
			logger.logGeneral.log({}, 'Transaction already prepared or in-flight, skipping...');
			return;
		}
		logger.logGeneral.log({}, '[prepareTransaction] proceeding with API call');
		isPreparingTransaction.current = true;

		const prepareTransactionRequest = (() => {
			switch (ppType) {
				case PPConfigType.GrantStreet:
					return {
						accountsToPay: cartItems.map((item) => ({
							accountNumber: item.item.itemNumber,
							amount: item.amountToPay,
						})),
						paymentType: 'ECHECK',
						location: kioskLocation,
						totalAmount: cartTotalAmount,
						payorInfo: payorInformation,
					};
				case PPConfigType.InvoiceCloudV1:
					return {
						accountsToPay: cartItems.map((item) => ({
							accountNumber: item.item.itemNumber,
							amount: item.amountToPay,
							invoiceGuid: item.item.itemGuid,
							icAchPaymentId: item.item.icAchPaymentId,
						})),
						paymentType: 'ECHECK',
						location: kioskLocation,
						totalAmount: cartTotalAmount,
						payorInfo: payorInformation,
					};
				case PPConfigType.PayGo:
					return {
						accountsToPay: cartItems.map((item) => ({
							accountNumber: item.item.itemNumber,
							amount: item.amountToPay,
						})),
						paymentType: 'ECHECK',
						location: kioskLocation,
						totalAmount: cartTotalAmount,
						payorInfo: payorInformation,
					};
				case PPConfigType.MyUsage:
					return {
						accountsToPay: cartItems.map((item) => ({
							accountNumber: item.item.itemNumber,
							amount: item.amountToPay,
						})),
						paymentType: 'ECHECK',
						location: kioskLocation,
						totalAmount: cartTotalAmount,
						payorInfo: payorInformation,
					};
			}
		})();

		if (!prepareTransactionRequest) {
			logger.logGeneral.error({}, 'No prepare transaction request could be constructed', 'No prepare transaction request could be constructed');
			throw new Error('No prepare transaction request could be constructed');
		}

		try {
			logger.logGeneral.log({}, '[prepareTransaction] calling api.payments.prepareTransaction...');
			const data = await api.payments.prepareTransaction(prepareTransactionRequest);
			const fees = data?.fees?.eCheck?.fees;

			if (data?.cartId) {
				logger.logGeneral.log({}, `[prepareTransaction] setting cartId: ${data.cartId}`);
				setCartId(data.cartId);
				setToken(data.token!);
			}

			if (fees) {
				logger.logGeneral.log({}, `[prepareTransaction] fees found, setting SERVICE_FEE state. fees: ${fees}`);
				setTransactionFee(fees);
				setPaymentState(PaymentCheckState.SERVICE_FEE);
			} else {
				logger.logGeneral.log({}, '[prepareTransaction] no fees in response');
			}

			logger.logGeneral.log({}, '[prepareTransaction] setting hasPreparedTransaction = true');
			setHasPreparedTransaction(true);
			setIsPrepared(true); // <-- triggers React re-render
		} catch (error) {
			logger.logGeneral.error({}, '[prepareTransaction] ERROR:', error);
			logger.logGeneral.error({}, 'Failed to prepare transaction:', error);
			// Reset ref on error so it can be retried
			isPreparingTransaction.current = false;
		}
	}, [
		ppType,
		hasPreparedTransaction,
		setHasPreparedTransaction,
		cartItems,
		kioskLocation,
		cartTotalAmount,
		payorInformation,
		setCartId,
		setToken,
		setTransactionFee,
	]);

	useEffect(() => {
		logger.logGeneral.log({}, 'ppType:', ppType);
		switch (ppType) {
			case PPConfigType.GrantStreet:
				prepareTransaction();
				break;
			case PPConfigType.InvoiceCloudV1:
				prepareTransaction();
				break;
			case PPConfigType.PayGo:
				prepareTransaction();
				break;
			case PPConfigType.MyUsage:
				prepareTransaction();
				break;
			default:
				break;
		}
	}, [ppType, prepareTransaction]);

	const handleCheckPayment = useCallback(
		async (deviceCheckData: CheckData) => {
			logger.logGeneral.log(
				{},
				`[handleCheckPayment] called — ppType: ${ppType}, hasPreparedTransactionRef: ${hasPreparedTransactionRef.current}`
			);
			const requiresPrepare =
				ppType === PPConfigType.GrantStreet ||
				ppType === PPConfigType.InvoiceCloudV1 ||
				ppType === PPConfigType.PayGo ||
				ppType === PPConfigType.MyUsage;
			if (requiresPrepare && !hasPreparedTransactionRef.current) {
				logger.logGeneral.log(
					{},
					`[handleCheckPayment] BAILING — requiresPrepare: ${requiresPrepare}, hasPreparedTransactionRef: ${hasPreparedTransactionRef.current}`
				);
				return;
			}

			const updateCheckData = (
				checkData: CheckData,
				accountNumber: string | null,
				routingNumber: string | null,
				checkNumber: string | null
			) => {
				const updatedCheckData = { ...checkData };
				if (accountNumber) updatedCheckData.account = accountNumber;
				if (routingNumber) updatedCheckData.routing = routingNumber;
				if (checkNumber) updatedCheckData.checkNumber = checkNumber;
				return updatedCheckData;
			};

			const handleTestCheckData = (data: CheckData): CheckData => {
				try {
					logger.logGeneral.log({}, 'getting kio environment', environment);
					switch (environment) {
						case AppEnvironment.Dev:
						case AppEnvironment.QA:
						case AppEnvironment.Demo: {
							logger.logGeneral.log({}, 'in demo environment - applying test check number substitutions');
							const accountNumber =
								KioAppHelper('DoUrlSubstitution', '$$BPK_TEST_CHK_ACC_NUM$$') || import.meta.env.VITE_TEST_ACC_NUMBER;
							const routingNumber =
								KioAppHelper('DoUrlSubstitution', '$$BPK_TEST_CHK_RTN_NUM$$') || import.meta.env.VITE_TEST_ROUTING_NUMBER;
							const checkNumber = Math.floor(Math.random() * 9999)
								.toString()
								.padStart(4, '0');
							const newCheckData = updateCheckData(data, accountNumber, routingNumber, checkNumber);
							logger.logGeneral.log({}, 'Applied test check number substitutions', newCheckData);
							return newCheckData;
						}
					}
					return data;
				} catch (error) {
					logger.logGeneral.error({}, 'Error applying test check number substitutions', error);
					return data;
				}
			};

			const checkData: CheckData = handleTestCheckData(deviceCheckData);
			console.log('Processed check data for payment:', checkData);

			if (paymentState !== PaymentCheckState.AWAITING_INSERT) {
				logger.logGeneral.log({}, 'Invalid state for handling check payment', 'Invalid state for handling check payment');
				return;
			}

			if (!checkData) {
				logger.logGeneral.log({}, 'No check data available for processing payment', 'No check data available for processing payment');
				return;
			}

			// const wasCheckInserted = checkReader?.checkInserted() ?? false;

			const item = cartItems[0];
			if (!item.customer) item.customer = { customerId: cartId || '', name: `${payorInformation?.firstName} ${payorInformation?.lastName}` };
			let response: PayWithCheckResponse | null = null;
			try {
				setCanCancel(false);
				showLoader();
				setPaymentState(PaymentCheckState.PROCESSING);
				logger.logGeneral.log({}, 'setting up check payment request');
				const identifier = cartId ? cartId : item.id!;
				const utilityAccountNumber =
					ppType === PPConfigType.MyUsage || ppType === PPConfigType.PayGo
						? item.item.itemNumber
						: identifier;
				const resolvedCheckNumber =
					checkData.checkNumber?.trim() || extractCheckNumberFromMicr(checkData.micr) || '';
				const request: PayWithCheckRequest = {
					utilityAccountNumber,
					accountToken: item.item.accountToken,
					amountToPay: cartTotalAmount!,
					amountDue: cartTotalAmount!,
					isPrePay: supportsPrePayFlag ? item.item.isPrePay : undefined,
					serviceFee: transactionFee ? parseAmountToNumber(transactionFee) : 0,
					customerName: item!.item.customerName ?? item!.customer?.name ?? '',
					routingNumber: checkData.routing,
					accountNumber: checkData.account,
					checkNumber: resolvedCheckNumber,
					micrData: checkData.micr,
					// Local scanner file paths are not base64; omit until image upload is implemented.
					checkImage: undefined,
					terminalCity: kioskLocation == 'kiosk_mis' ? 'MISS' : 'KATY',
					terminalState: 'TX', // Placeholder, replace with actual state
					zip: item.item.zip,
					auxiliaryOnUs: checkData.micr.includes('*'), // If MICR contains asterisk, it has the on-us field
					cartId: cartId!,
					token: token!,
					invoiceGuid: item.item.itemGuid,
					paymentTypeId: item.item.icAchPaymentId,
					group: item.item.group ?? undefined,
					classification: item.item.classification ?? undefined,
				};

				const legacyPostAcceptedPayment: LegacyPostAcceptedPayment = structuredClone({
					dynatouchGUID: legacyId!,
					firstCashTransaction: false,
					customerID: companyId.toString(),
					kioskID: kioskId.toString(),
					paymentGatewayExternalID: item!.item.itemNumber,
					paymentDateTime: new Date().toISOString(),
					paymentAmount: cartTotalAmount! + (transactionFee ? parseAmountToNumber(transactionFee) : 0),
					paymentType: 'Check',
					paymentGUID: guid(),
					passKey: await passKeyHelper.generatePassKey(),
					cashOnes: 0,
					cashTwos: 0,
					cashFives: 0,
					cashTens: 0,
					cashTwenties: 0,
					cashFifties: 0,
					cashHundreds: 0,
					cisProvider: enumHelper.getCisConfigTypeLabel(cisType),
					paymentGateway: enumHelper.getPpConfigTypeLabel(ppType),
					cisProviderExternalID: item!.id!,
					payorName: item!.item.customerName ?? item!.customer?.name ?? '',
				});

				const legacyPostRejectedPayment: LegacyPostRejectedPayment = structuredClone({
					customerID: companyId.toString(),
					kioskID: kioskId.toString(),
					billpaySessionGUID: legacyId!,
					rejectedECID: '',
					rejectedReason: 'Check payment error',
					rejectedPaymentType: 'Check',
					paymentAmount: cartTotalAmount! + (transactionFee ? parseAmountToNumber(transactionFee) : 0),
					payorName: item!.item.customerName ?? item!.customer?.name ?? '',
					paymentDateTime: new Date().toISOString(),
					passKey: await passKeyHelper.generatePassKey(),
				});
				setBackEnabled(false);
				response = await api.payments.check(request, legacyPostAcceptedPayment, legacyPostRejectedPayment);
				if (!response.paymentId) throw new Error('No payment ID returned from check payment API');
				logger.logCheckPayment.log(
					{
						paymentId: response!.paymentId,
						customerName: item!.item.customerName ?? item!.customer?.name ?? '',
						cISConfig: cisType,
						pPConfig: ppType,
						referenceNumberLabel: response!.referenceNumberLabel,
						referenceNumberValue: response!.referenceNumberValue,
						amountToPay: item!.amountToPayWithFee!,
						checkAmount: item!.amountToPayWithFee!,
					},
					'Check payment successful'
				);
				if (enablePreReceiptCheckRemovalPrompt) {
					setPreReceiptCheckRemovalPromptHandled(true);
					setPaymentState(PaymentCheckState.AWAITING_REMOVAL);
				} else {
					setPaymentState(PaymentCheckState.DONE);
				}
			} catch (error) {
				setError({ message: getApiErrorMessage(error, getString('check-payment-error')) });
				setPaymentState(PaymentCheckState.ERROR);
				logger.logCheckPayment.error(
					{
						paymentId: response?.paymentId ?? '',
						customerName: item?.item.customerName ?? item?.customer?.name ?? '',
						cISConfig: cisType,
						pPConfig: ppType,
						referenceNumberLabel: response?.referenceNumberLabel ?? '',
						referenceNumberValue: response?.referenceNumberValue ?? '',
						amountToPay: item?.amountToPayWithFee ?? 0,
						checkAmount: item?.amountToPayWithFee ?? 0,
					},
					'Error processing check payment:',
					error
				);
			} finally {
				setIsCleanupRequired(false);
				unregisterCleanup(PAYMENT_CHECK_REGISTRY_KEY);
				setCanCancel(true);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[cartItems, legacyId, paymentState, checkReader, enablePreReceiptCheckRemovalPrompt]
	);

	useSessionCleanup(
		PAYMENT_CHECK_REGISTRY_KEY,
		useCallback(async () => {
			if (!isCleanupRequired) return;
			logger.logGeneral.warn({}, 'Cleaning up incomplete check payment session');

			try {
				if (devices.checkReader) devices.checkReader.cancelRead();
				if (devices.checkReaderStub) devices.checkReaderStub.cancelRead();
			} catch (error) {
				logger.logGeneral.error({}, 'Error cancelling check read during cleanup', error);
			}
		}, [isCleanupRequired])
	);

	useEffect(() => {
		const checkReaderOk =
			devices.checkReader.getStatus().statusBits === DeviceStatusFlags.Ok && (config.ignoreCheckSensor || !devices.checkReader.checkInserted());
		logger.logGeneral.log({}, 'Check reader status in payment-check ok:', checkReaderOk);
		if (config.ignoreCheckSensor) {
			logger.logGeneral.log({}, 'Ignoring check sensor - forcing real check reader device');
		}
		switch (envDeviceOption) {
			case 'always':
				setCheckReader(devices.checkReader);
				break;
			case 'never':
				setCheckReader(devices.checkReaderStub);
				break;
			case 'with-fallback':
				if (checkReaderOk) {
					setCheckReader(devices.checkReader);
					logger.logGeneral.log({}, 'Using check reader device for check payments');
				} else {
					setCheckReader(devices.checkReaderStub);
					logger.logGeneral.log({}, 'Check reader device not available, using stub for check payments');
				}
				break;
		}
	}, [envDeviceOption, config.ignoreCheckSensor]);

	useEffect(() => {
		return () => {
			checkReader?.cancelRead();
		};
	}, [checkReader]);

	useEffect(() => {
		if (!checkReader) {
			logger.logGeneral.log({}, '[checkReaderEffect] no checkReader, returning early');
			return;
		}

		let waitForTakeCheckInterval = 0;
		let retryTimeout: ReturnType<typeof setTimeout> | null = null;
		let aborted = false;

		switch (paymentState) {
			case PaymentCheckState.AWAITING_INSERT: {
				logger.logGeneral.log({}, '[checkReaderEffect] AWAITING_INSERT — setting up scanner');

				clearCustomBackAction();
				setBackEnabled(true);

				if (config.ignoreCheckSensor) {
					// === IGNORE SENSOR MODE ===
					// Cancel any prior read to reset device state (unreliable sensor workaround)
					// then issue a new read with retry logic on failure
					const initiateRead = () => {
						if (aborted) {
							logger.logGeneral.log({}, '[initiateRead] aborted, returning');
							return;
						}

						logger.logGeneral.log({}, '[initiateRead] calling checkReader.readCheck...');
						checkReader.readCheck(!!stampVoid, (err, imagePath, micr, account, routing) => {
							logger.logGeneral.log({}, `[initiateRead] readCheck callback — err: ${err}, aborted: ${aborted}`);
							if (aborted) return;

							if (err === DeviceStatusFlags.Ok) {
								setError(null);
								setCheckData({ imagePath, micr, account, routing });
								setIsCleanupRequired(true);
								handleCheckPayment({ imagePath, micr, account, routing });
							} else {
								logger.logGeneral.error(
									{},
									'Check reading error (ignoreCheckSensor mode)',
									'Check reading error (ignoreCheckSensor mode)'
								);
								logger.logCheckPayment.error(
									{
										paymentId: cartId ?? '',
										customerName: `${payorInformation?.firstName || 'No first name'} ${payorInformation?.lastName || 'No last name'}`,
										cISConfig: CISConfigType.None,
										pPConfig: PPConfigType.None,
										referenceNumberLabel: '',
										referenceNumberValue: '',
										amountToPay: 0,
										checkAmount: 0,
									},
									`Error reading check: ${err}`,
									err.toString()
								);
								setError({ message: getString('check-reading-error') });
								setPaymentState(PaymentCheckState.ERROR);
							}
						});
					};

					logger.logGeneral.log({}, '[checkReaderEffect] ignoreCheckSensor — cancelling read and scheduling initiateRead in 500ms');
					checkReader.cancelRead();
					retryTimeout = setTimeout(() => {
						initiateRead();
					}, 500);
				} else {
					// === NORMAL MODE (original behavior, unchanged) ===
					checkReader.readCheck(!!stampVoid, (err, imagePath, micr, account, routing) => {
						if (err === DeviceStatusFlags.Ok) {
							setError(null);
							setCheckData({ imagePath, micr, account, routing });
							setIsCleanupRequired(true);
							handleCheckPayment({ imagePath, micr, account, routing });
						} else {
							logger.logGeneral.error({}, 'Check reading error', 'Check reading error');
							logger.logCheckPayment.error(
								{
									paymentId: cartId ?? '',
									customerName: `${payorInformation?.firstName || 'No first name'} ${payorInformation?.lastName || 'No last name'}`,
									cISConfig: CISConfigType.None,
									pPConfig: PPConfigType.None,
									referenceNumberLabel: '',
									referenceNumberValue: '',
									amountToPay: 0,
									checkAmount: 0,
								},
								`Error reading check: ${err}`,
								err.toString()
							);
							setError({ message: getString('check-reading-error') });
							setPaymentState(PaymentCheckState.ERROR);
						}
					});
				}
				break;
			}
			case PaymentCheckState.ERROR:
				// Error state will show retry options
				break;
			case PaymentCheckState.PROCESSING:
				// Processing state will show spinner
				break;
			case PaymentCheckState.AWAITING_REMOVAL:
				waitForTakeCheckInterval = setInterval(() => {
					if (!checkReader?.checkInserted()) {
						const startedAt = takeCheckReminderStartedAtRef.current;
						if (startedAt && Date.now() - startedAt >= TAKE_CHECK_REMINDER_MIN_MS) {
							clearInterval(waitForTakeCheckInterval);
							waitForTakeCheckInterval = 0;
							setPaymentState(PaymentCheckState.DONE);
						}
					}
				}, WAIT_FOR_TAKE_CHECK_MS);
				break;
			case PaymentCheckState.SERVICE_FEE:
				//checkReader.cancelRead();
				setPaymentState(PaymentCheckState.SERVICE_FEE);
				break;
			case PaymentCheckState.DONE:
				handleNavigation(nextPageTarget);
				break;
		}

		return () => {
			logger.logGeneral.log({}, '[checkReaderEffect] CLEANUP — setting aborted=true, clearing timeouts');
			aborted = true;
			if (waitForTakeCheckInterval) clearInterval(waitForTakeCheckInterval);
			if (retryTimeout) clearTimeout(retryTimeout);
		};
	}, [
		cartId,
		checkData,
		checkReader,
		handleCheckPayment,
		handleNavigation,
		nextPageTarget,
		paymentState,
		payorInformation?.firstName,
		payorInformation?.lastName,
		stampVoid,
		hasPreparedTransaction,
		clearCustomBackAction,
		setBackEnabled,
		config.ignoreCheckSensor,
	]);

	const handleRetry = () => {
		setError(null);
		setPaymentState(PaymentCheckState.AWAITING_INSERT);
	};

	const handleCancel = () => {
		setError(null);
		setPaymentState(PaymentCheckState.AWAITING_INSERT);
		handleNavigation(previousPageTarget);
	};

	const handleServiceFeeConfirm = () => {
		// Only cancel the read if ignoring the check sensor
		// to reset device state for unreliable sensors
		if (config.ignoreCheckSensor && checkReader) {
			checkReader.cancelRead();
		}
		setPaymentState(PaymentCheckState.AWAITING_INSERT);
	};

	const handleServiceFeeCancel = () => {
		handleNavigation(previousPageTarget);
	};

	const renderPayView = () => (
		<section className={style.section}>
			<img className={style.section__image} src={checkScannerImage} alt={imageAltKey} />
			<BalancesKvpList
				className={style.section__details}
				kvpList={[]}
				totalSum={{
					label: getString(paymentTotalLabelKey ?? ''),
					value: (cartTotalAmount ?? 0) + (transactionFee ? parseAmountToNumber(transactionFee) : 0),
					forceRender: true,
				}}
			/>
			<MessageBoxSet
				className={style.section__messages}
				infoText={error ? `${getString(errorLabel ?? '')}.\n${getString(infoBoxLabelKey ?? '')}` : getString(infoBoxLabelKey ?? '')}
				warningText={getString(warningBoxLabelKey ?? '')}
			/>
		</section>
	);

	const renderErrorView = () => (
		<section className={style.error}>
			<MainMessageBox icon={AppIconType.Error} message={error?.message ?? getString('checkErrorGeneric')} />
			<Confirm
				titleLabel={getString('try-again-message')}
				yesButton={{ label: getString('yes'), value: 'yes', target: '' }}
				noButton={{ label: getString('no'), value: 'no', target: '' }}
				yesCallback={handleRetry}
				noCallback={handleCancel}
			/>
		</section>
	);

	const renderServiceFeeView = () => {
		logger.logGeneral.log({}, 'Rendering service fee view');
		return (
			<ServiceFeeView
				amount={cartTotalAmount}
				fee={parseAmountToNumber(transactionFee!)}
				onConfirm={handleServiceFeeConfirm}
				onCancel={handleServiceFeeCancel}
				labels={
					paymentFeeViewServerProps
						? paymentFeeViewServerProps
						: ({ titleLabelKey: 'service-fee-description-check' } as PaymentFeeViewServerProps)
				}
			/>
		);
	};

	const renderGetCheckView = () => <GetCheckView doneCallback={() => setPaymentState(PaymentCheckState.DONE)} />;

	const renderCurrentView = () => {
		switch (paymentState) {
			case PaymentCheckState.SERVICE_FEE:
				return renderServiceFeeView();
			case PaymentCheckState.AWAITING_INSERT:
				return renderPayView();
			case PaymentCheckState.ERROR:
				return renderErrorView();
			case PaymentCheckState.AWAITING_REMOVAL:
				return renderGetCheckView();
		}
	};

	return <section className={style.section}>{renderCurrentView()}</section>;
};

export default PaymentCheck;
